import queryString from 'query-string';
import React, { useContext, useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { getUserInformationWithConfig, isUserAuthenticated, loginRedirectWithConfig, parseEncodedStateProperties, tradeAuthCodeWithConfig, defaultAppConfig } from '../util/AuthUtils';
import BasicUserConfig from '../components/userConfig/BasicUserConfig';
import LocalizationContext from '../context/LocalizationContext';

/**
 * React hook for authenticating with Hyrule Auth Server.
 * @param authTokenCallback - Callback function to be called once
 * app has been authenticated.
 * @param hyruleAppConfig - The Hyrule app config to use. If not provided the default of window.config is used.
 * @return Returns all data associated with Hyrule auth.
 */
export const useHyruleAuth = (authTokenCallback, hyruleAppConfig) => {
  const appConfig = hyruleAppConfig || defaultAppConfig;
  const [authState, setAuthState] = useState({
    authToken: null,
    authenticated: false,
    authenticating: true,
    userInfo: null,
    scopes: [],
    routeState: null
  });
  const queryParameters = queryString.parse(window.location.search);
  useEffect(() => {
    const authenticate = async () => {
      if (queryParameters && queryParameters.code) {
        const state = parseEncodedStateProperties(queryParameters.state);
        const tokenResponse = await tradeAuthCodeWithConfig(queryParameters.code, appConfig, state.sessionStorageId);
        const userInfo = await getUserInformationWithConfig(appConfig);
        setAuthState({
          authToken: tokenResponse.accessToken,
          scopes: tokenResponse.scopes,
          authenticated: true,
          authenticating: false,
          routeState: state.route || null,
          userInfo
        });
        if (authTokenCallback) {
          // If the user provided a callback, call it with the auth token
          authTokenCallback(tokenResponse);
        }
      } else if (isUserAuthenticated()) {
        // If the user is expected to be authenticated, automatically perform the OAuth Redirect
        loginRedirectWithConfig(appConfig);
      } else {
        setAuthState({
          ...authState,
          authenticated: false,
          authenticating: false
        });
      }
    };
    authenticate();
  }, []);
  return authState;
};

/**
 * Hyrule hook for generating routes based on the input.
 * @param tabs - Hyrule component config items
 * @param hiddenRoutes - Additional hidden routes
 * @param userConfig - optional custom component for user config.
 * @return - Returns react-router routes for each of the tab.
 */
export const useHyruleNav = (tabs, hiddenRoutes, userConfig) => {
  const getDefaultUserConfig = () => ({
    name: localizedStrings.hyrule.userProfile.edit,
    scopes: null,
    path: '/edit-profile',
    component: /*#__PURE__*/React.createElement(BasicUserConfig, null)
  });
  const localizedStrings = useContext(LocalizationContext);
  const [routes, setRoutes] = useState([]);
  const [configComponent, setConfigComponent] = useState(getDefaultUserConfig());
  useEffect(() => {
    let config;

    // Collect all HyruleNav tabs into routes
    for (let i = 0; i < tabs.length; i++) {
      const menuItem = tabs[i];
      for (let j = 0; j < menuItem.components.length; j++) {
        const componentConfig = menuItem.components[j];
        const newRoute = /*#__PURE__*/React.createElement(Route, {
          path: componentConfig.path,
          exact: !!componentConfig.exact,
          key: componentConfig.path,
          component: () => componentConfig.component
        });
        routes.push(newRoute);
      }
    }

    // Add in extra routes provided by the app
    if (hiddenRoutes) {
      hiddenRoutes.forEach(routeConfig => {
        const route = /*#__PURE__*/React.createElement(Route, {
          path: routeConfig.path,
          exact: !!routeConfig.exact,
          key: routeConfig.path,
          component: () => routeConfig.component
        });
        routes.push(route);
      });
    }

    // Uses custom user config if given
    if (userConfig) {
      config = userConfig;
      const route = /*#__PURE__*/React.createElement(Route, {
        path: userConfig.path,
        exact: !!userConfig.exact,
        key: userConfig.path,
        component: () => userConfig.component
      });
      routes.push(route);
    } else {
      const defaultConfig = getDefaultUserConfig();
      config = defaultConfig;
      const route = /*#__PURE__*/React.createElement(Route, {
        path: defaultConfig.path,
        key: defaultConfig.path,
        component: () => defaultConfig.component
      });
      routes.push(route);
    }
    setConfigComponent(config);
    setRoutes(routes);
  }, [localizedStrings.getLanguage()]);
  return {
    tabs,
    routes,
    configComponent
  };
};

/**
 * Hyrule Hook for syncing the current language between the client app and Hyrule platform. If the UserInfo object is null
 * (as in the user has not been authenticated yet), the language will remain the default (en-US). Once the user has been
 * aunthenticated and the user has a preferred language, both the Hyrule client app and Hyrule itself will be set to
 * the same language.
 *
 * @param userInfo - User Info data from Hyrule Auth
 * @param appLocalizationContext - LocalizationContext for the client app
 * @returns - Returns true once the user is authenticated and language is set.
 */
export const useHyruleLanguage = (userInfo, appLocalizationContext) => {
  const [languageWasSet, setLanguageWasSet] = useState(false);
  const hyruleText = useContext(LocalizationContext);
  const appText = useContext(appLocalizationContext);
  useEffect(() => {
    if (!languageWasSet && userInfo && userInfo.userSettings.baseSettings.locale && hyruleText.getLanguage() !== userInfo.userSettings.baseSettings.locale) {
      appText.setLanguage(userInfo.userSettings.baseSettings.locale);
      hyruleText.setLanguage(userInfo.userSettings.baseSettings.locale);
      setLanguageWasSet(true);
    }
  }, [userInfo === null || userInfo === void 0 ? void 0 : userInfo.userSettings.baseSettings.locale]);
  return languageWasSet;
};